import React, { useEffect } from "react";
import { Text } from "@fluentui/react-northstar";
import calendarIcon from "../../../images/print/ticket-details/calendar.png";
import userIcon from "../../../images/print/ticket-details/user.png";
import categoryIcon from "../../../images/print/ticket-details/category.png";
import groupIcon from "../../../images/print/ticket-details/group.png";
import templateIcon from "../../../images/print/ticket-details/template.png";
import tagIcon from "../../../images/print/ticket-details/tag.png";
import priorityIcon from "../../../images/print/ticket-details/priority.png";
import teamIcon from "../../../images/print/ticket-details/team.png";
import typeIcon from "../../../images/print/ticket-details/type.png";
import clockIcon from "../../../images/print/ticket-details/clock.png";
import relatedIcon from "../../../images/print/ticket-details/related.png";
import lifecycleIcon from "../../../images/print/ticket-details/lifecycle.png";
import affectedUserIcon from "../../../images/print/ticket-details/affected.png";
import { useTranslation } from "react-i18next";
import { TEMPLATE, TICKET_DETAIL } from "../../shared/utils/constants";
import { ConvertDateToLocale } from "../../shared/common/ConvertDate";
import { DateTime } from "luxon";
import { getCachedUsers } from "../../shared/cache/PlatformUserCache";

type TicketPagePrintDetailsProps = {
  ticket: Ticket;
  relatedTickets: string;
  isEndUser?: boolean;
};

export const TicketPagePrintDetails: React.FC<TicketPagePrintDetailsProps> = ({
  ticket,
  relatedTickets,
  isEndUser = false
}) => {
  const { t } = useTranslation();
  const [affectedUsers, setAffectedUsers] = React.useState<PlatformUser[]>([]);
  useEffect(() => {
    (async () => {
      const users = await getCachedUsers(ticket.AffectedUsers.map(x => x.PlatformUserId))
      setAffectedUsers(users);
    })();
  }, []);

  const renderRequesterItems = () => {
    return (
      <>
        <div className="flex items-center flex-grow w-1/4 border-r border-b p-1">
          <img src={userIcon} alt="user icon" width="12px" className="mr-2" />
          <Text size="smaller" weight="semibold" className="capitalize">
            {TICKET_DETAIL.REQUESTER}
          </Text>
        </div>
        <div className="flex items-center flex-grow w-1/4 border-r border-b p-1">
          <Text size="smaller">{ticket.Requester?.FullName ?? "-"}</Text>
        </div>
      </>
    )
  }

  const renderCreatedDateItems = () => {
    return (
      <>
        <div className="flex items-center flex-grow w-1/4 border-r border-b p-1">
          <img
            src={calendarIcon}
            alt="calendar icon"
            width="12px"
            className="mr-2"
          />
          <Text size="smaller" weight="semibold">
            {t("ticket.ticket-list.created-date")}
          </Text>
        </div>
        <div className="flex items-center flex-grow w-1/4 border-r border-b p-1">
          <Text size="smaller">
            {ConvertDateToLocale(
              ticket.CreatedDate,
              DateTime.DATETIME_MED_WITH_SECONDS
            )}
          </Text>
        </div>
      </>
    )
  }

  const renderAssigneeItems = () => {
    return (
      <>
        <div className="flex items-centerflex-grow w-1/4 border-r border-b p-1">
          <img src={userIcon} alt="user icon" width="12px" className="mr-2" />
          <Text size="smaller" weight="semibold" className="capitalize">
            {TICKET_DETAIL.ASSIGNEE}
          </Text>
        </div>
        <div className="flex items-center flex-grow w-1/4 border-r border-b p-1">
          <Text size="smaller">
            {ticket.Assignee?.FullName ?? '-'}
          </Text>
        </div>
      </>
    )
  }

  const renderPriorityItems = () => {
    if (isEndUser) {
      return null;
    }

    return (
      <>
        <div className="flex items-center flex-grow w-1/4 border-r border-b p-1">
          <img
            src={priorityIcon}
            alt="priority icon"
            width="12px"
            className="mr-2"
          />
          <Text size="smaller" weight="semibold" className="capitalize">
            {TICKET_DETAIL.PRIORITY}
          </Text>
        </div>
        <div className="flex items-center flex-grow w-1/4 border-r border-b p-1">
          <Text size="smaller">{ticket.Priority?.Value ?? "-"}</Text>
        </div>
      </>
    )
  }

  const renderCategoryItems = () => {
    if (isEndUser) {
      return null;
    }

    return (
      <>
        <div className="flex items-center flex-grow w-1/4 border-r border-b p-1">
          <img
            src={categoryIcon}
            alt="category icon"
            width="12px"
            className="mr-2"
          />
          <Text size="smaller" weight="semibold" className="capitalize">
            {TICKET_DETAIL.CATEGORY}
          </Text>
        </div>
        <div className="flex items-center flex-grow w-1/4 border-r border-b p-1">
          <Text size="smaller">{ticket.Category?.Value ?? "-"}</Text>
        </div>
      </>
    )
  }

  const renderTeamItems = () => {
    if (isEndUser) {
      return null;
    }

    return (
      <>
        <div className="flex items-center flex-grow w-1/4 border-r border-b p-1">
          <img src={teamIcon} alt="team icon" width="12px" className="mr-2" />
          <Text size="smaller" weight="semibold" className="capitalize">
            {TICKET_DETAIL.TEAM}
          </Text>
        </div>
        <div className="flex items-center flex-grow w-1/4 border-r border-b p-1">
          <Text size="smaller">{ticket.Team?.FriendlyName ?? "-"}</Text>
        </div>
      </>
    )
  }

  const renderSupportGroupItems = () => {
    if (isEndUser) {
      return null;
    }

    return (
      <>
        <div className="flex items-center flex-grow w-1/4 border-r border-b p-1">
          <img
            src={groupIcon}
            alt="group icon"
            width="12px"
            className="mr-2"
          />
          <Text size="smaller" weight="semibold" className="capitalize">
            {TICKET_DETAIL.GROUP}
          </Text>
        </div>
        <div className="flex items-center flex-grow w-1/4 border-r border-b p-1">
          <Text size="smaller">{ticket.SupportGroup?.Name ?? "-"}</Text>
        </div>
      </>
    )
  }

  const renderDueDateItems = () => {
    if (isEndUser) {
      return null;
    }

    return (
      <>
        <div className="flex items-center flex-grow w-1/4 border-r border-b p-1">
          <img
            src={calendarIcon}
            alt="calendar icon"
            width="12px"
            className="mr-2"
          />
          <Text size="smaller" weight="semibold" className="capitalize">
            {TICKET_DETAIL.DUE_DATE}
          </Text>
        </div>
        <div className="flex items-center flex-grow w-1/4 border-r border-b p-1">
          <Text size="smaller">
            {ticket.DueDate
              ? ConvertDateToLocale(
                ticket.DueDate,
                DateTime.DATETIME_MED_WITH_SECONDS
              )
              : "-"}
          </Text>
        </div>
      </>
    )
  }

  const renderTemplateItems = () => {
    if (isEndUser) {
      return null;
    }

    return (
      <>
        <div className="flex items-center flex-grow w-1/4 border-r border-b p-1">
          <img
            src={templateIcon}
            alt="template icon"
            width="12px"
            className="mr-2"
          />
          <Text size="smaller" weight="semibold" className="capitalize">
            {TICKET_DETAIL.TEMPLATE}
          </Text>
        </div>
        <div className="flex items-center flex-grow w-1/4 border-r border-b p-1">
          <Text size="smaller">{ticket.Template?.Name ?? "-"}</Text>
        </div>
      </>
    )
  }

  const renderTicketTypeItems = () => {
    if (isEndUser) {
      return null;
    }

    return (
      <>
        <div className="flex items-center flex-grow w-1/4 border-r border-b p-1">
          <img src={typeIcon} alt="type icon" width="12px" className="mr-2" />
          <Text size="smaller" weight="semibold" className="capitalize">
            {TICKET_DETAIL.TICKET_TYPE}
          </Text>
        </div>
        <div className="flex items-center flex-grow w-1/4 border-r border-b p-1">
          <Text size="smaller">{ticket.TicketType?.Value ?? "-"}</Text>
        </div>
      </>
    )
  }

  const renderTagItems = () => {
    if (isEndUser) {
      return null;
    }

    return (
      <>
        <div className="flex items-center flex-grow w-1/4 border-r border-b p-1">
          <img src={tagIcon} alt="tag icon" width="12px" className="mr-2" />
          <Text size="smaller" weight="semibold" className="capitalize">
            {TICKET_DETAIL.TAGS}
          </Text>
        </div>
        <div className="flex items-center flex-grow w-1/4 border-r border-b p-1">
          <Text size="smaller">
            {ticket?.Tags?.length
              ? ticket.Tags.map((t: any) => t.Name).join(", ")
              : "-"}
          </Text>
        </div>
      </>
    )
  }

  const renderSlaItems = () => {
    if (isEndUser) {
      return null;
    }

    return (
      <>
        <div className="flex items-center flex-grow w-1/4 border-r border-b p-1">
          <img
            src={clockIcon}
            alt="clock icon"
            width="12px"
            className="mr-2"
          />
          <Text size="smaller" weight="semibold" className="capitalize">
            {TICKET_DETAIL.SLAS}
          </Text>
        </div>
        <div className="flex items-center flex-grow w-1/4 border-r border-b p-1">
          <Text size="smaller">
            {ticket.TicketSlas.length
              ? ticket.TicketSlas.map(
                (t: any) => t.ServiceLevelAgreement.Name
              ).join(", ")
              : "-"}
          </Text>
        </div>
      </>
    )
  }

  const renderLastModifiedDateItems = () => {
    return (
      <>
        <div className="flex items-center flex-grow w-1/4 border-r border-b p-1">
          <img
            src={calendarIcon}
            alt="calendar icon"
            width="12px"
            className="mr-2"
          />
          <Text size="smaller" weight="semibold" className="capitalize">
            {t("ticket.ticket-list.last-modified-date")}
          </Text>
        </div>
        <div className="flex items-center flex-grow w-1/4 border-r border-b p-1">
          <Text size="smaller">
            {ticket.ModifiedDate
              ? ConvertDateToLocale(
                ticket.ModifiedDate,
                DateTime.DATETIME_MED_WITH_SECONDS
              )
              : "-"}
          </Text>
        </div>
      </>
    )
  }

  const renderRelatedTicketItems = () => {
    if (isEndUser) {
      return null;
    }

    return (
      <>
        <div className="flex items-center flex-grow w-1/4 border-r border-b p-1">
          <img
            src={relatedIcon}
            alt="related icon"
            width="12px"
            className="mr-2"
          />
          <Text size="smaller" weight="semibold" className="capitalize">
            {t("automation.automation-properties.relatedtickets")}
          </Text>
        </div>
        <div className="flex items-center flex-grow w-1/4 border-r border-b p-1">
          <Text size="smaller">{relatedTickets === "" ? "-" : relatedTickets}</Text>
        </div>
      </>
    )
  }

  const renderAffectedUsersItems = () => {
    return (
      <>
        <div className="flex items-center flex-grow w-1/4 border-r border-b p-1">
          <img
            src={affectedUserIcon}
            alt="affected user icon"
            width="12px"
            className="mr-2"
          />
          <Text size="smaller" weight="semibold" className="capitalize">
            {t("ticket.ticket-list.affected-users")}
          </Text>
        </div>
        <div className="flex items-center flex-grow w-1/4 border-r border-b p-1">
          <Text size="smaller">
            {ticket.AffectedUsers.length
              ? affectedUsers.map((au) => au.FullName).join(", ")
              : "-"}
          </Text>
        </div>
      </>
    )

  }

  const renderTicketLifecycleItems = () => {
    if (isEndUser) {
      return null;
    }

    return (
      <>
        <div className="flex items-center flex-grow w-1/4 border-r border-b p-1">
          <img
            src={lifecycleIcon}
            alt="lifecycle icon"
            width="12px"
            className="mr-2"
          />
          <Text size="smaller" weight="semibold" className="capitalize">
            {t("ticket.ticket-list.lifecycle")}
          </Text>
        </div>
        <div className="flex items-center flex-grow w-1/4 border-r border-b p-1">
          <Text size="smaller">{ticket.TicketLifecycle?.Title ?? "-"}</Text>
        </div>
      </>
    )
  }

  return (
    <div
      className="hidden print-block print-text-color mb-4"
      style={{ pageBreakInside: "avoid" }}
    >
      <div className="mt-3 mb-2">
        <Text size="small" weight="semibold" className="capitalize">
          {TEMPLATE.TICKET_PROPERTIES}
        </Text>
      </div>

      <div className="border div-table">
        <div className="flex w-full border-b p-2">
          <Text size="smaller">{ticket.Title}</Text>
        </div>
        <div className="flex flex-row flex-wrap">
          {renderRequesterItems()}
          {renderCreatedDateItems()}
          {renderAssigneeItems()}
          {renderPriorityItems()}
          {renderCategoryItems()}
          {renderTeamItems()}
          {renderSupportGroupItems()}
          {renderDueDateItems()}
          {renderTemplateItems()}
          {renderTicketTypeItems()}
          {renderTagItems()}
          {renderSlaItems()}
          {renderLastModifiedDateItems()}
          {renderRelatedTicketItems()}
          {renderAffectedUsersItems()}
          {renderTicketLifecycleItems()}
        </div>
      </div>
    </div>
  );
};
